import moment from 'moment';
import { Space, Row } from 'antd';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Button from 'components/new-ui/base/button/Button';
import DatePicker from 'components/new-ui/base/date-picker/DatePicker';
import SearchBar from 'components/new-ui/base/searchbar/SearchBar';
import Notification from 'components/ui/base/notification/Notification';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import useTeams from 'hooks/useTeams';
import { archiveJob, unarchiveJob } from 'store/jobs/client';
import { JobResponse } from 'store/jobs/job';
import AppliedJobFilters from 'views/jobs/components/AppliedJobFilters';
import JobFiltersModal from 'views/jobs/components/JobFiltersModal';
import JobsTable from 'views/jobs/components/JobsTable';
import { useJobsFilters } from 'views/jobs/useJobsFiters';
import ArchiveJobModal from 'views/manifests/components/ArchiveJobModal';
import AssignJobModal from 'views/manifests/components/AssignJobModal';
import UnarchiveJobModal from 'views/manifests/components/UnarchiveJobModal';
import useIndividuals from 'hooks/useIndividuals';
import useCustomers from 'hooks/useCustomers';
import EditJobModal from 'views/jobs/components/edit-job/EditJobModal';

const JobsTab = () => {
    const { teamId } = useParams();
    const { archivationStatus, getTeamJobsCount } = useTeams(teamId);
    const navigate = useNavigate();
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();

    const { listIndividuals } = useIndividuals();
    const { listCustomers } = useCustomers();

    const [jobIdToArchive, setJobIdToArchive] = useState<string | null>(null);
    const [selectedJob, setSelectedJob] = useState<JobResponse | null>(null);
    const [modal, setModal] = useState<'archive' | 'unarchive' | 'assign' | 'filter' | 'edit' | null>(null);

    const closeModal = () => setModal(null);

    const {
        data,
        isLoading,
        refetch,
        setArchived,
        queryInputVal,
        exactDate,
        tags,
        clearFilter,
        clearAll,
        pagination,
        sortDirection,
        sort,
        filters,
        handleFiltersChange,
        handleTableChange,
        handleExactDateChange,
        handleSearch,
        handleClearSearch,
    } = useJobsFilters((params) => ({
        ...params,
        teamId,
        archived: archivationStatus === 'archived' ? true : undefined,
    }));

    const handleArchiveJob = async () => {
        if (!jobIdToArchive) {
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Error',
                description: 'No job selected',
            });
            return;
        }
        showGlobalOverlay({ type: 'LoadingOverlay' });
        try {
            await archiveJob(jobIdToArchive);
            Notification({
                type: 'success',
                placement: 'top',
                messageTitle: 'Job Archived',
                description: 'Successfully archived job',
            });
            await refetch();
            if (teamId) getTeamJobsCount(teamId);
        } catch {
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Error',
                description: 'Could not archive job',
            });
        } finally {
            hideGlobalOverlay();
            setJobIdToArchive(null);
            closeModal();
        }
    };

    const handleUnarchiveJob = async () => {
        if (!jobIdToArchive) {
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Error',
                description: 'No job selected',
            });
            return;
        }
        showGlobalOverlay({ type: 'LoadingOverlay' });
        try {
            await unarchiveJob(jobIdToArchive);
            Notification({
                type: 'success',
                placement: 'top',
                messageTitle: 'Job Unarchived',
                description: 'Successfully unarchived job',
            });
            await refetch();
            if (teamId) getTeamJobsCount(teamId);
        } catch {
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Error',
                description: 'Could not unarchive job',
            });
        } finally {
            hideGlobalOverlay();
            setJobIdToArchive(null);
            closeModal();
        }
    };

    // Sync the Archived switch filter with the URL params
    useEffect(() => {
        setArchived(archivationStatus === 'archived' ? true : false);
    }, [archivationStatus, setArchived]);

    // Refresh the resources in memory
    useEffect(() => {
        listIndividuals({ size: 200 });
        listCustomers({ size: 200 });
    }, [listIndividuals, listCustomers]);

    return (
        <>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Row justify="space-between">
                    <Space size={8}>
                        <SearchBar
                            inputPlaceholder="Search jobs"
                            value={queryInputVal}
                            onSearch={handleSearch}
                            onClear={handleClearSearch}
                        />
                        <Button onClick={() => setModal('filter')}>Filter</Button>
                    </Space>
                    <Space size={8}>
                        <DatePicker
                            value={exactDate ? moment(exactDate, 'YYYY-MM-DD') : undefined}
                            onChange={handleExactDateChange}
                        />
                    </Space>
                </Row>

                <Row>
                    <AppliedJobFilters tags={tags} clearFilter={clearFilter} clearAll={clearAll} />
                </Row>

                <JobsTable
                    isUnderManifest
                    loading={isLoading}
                    jobs={data?.list ?? []}
                    actions={{
                        assign: (job: JobResponse) => {
                            setSelectedJob(job);
                            setModal('assign');
                        },
                        archive: (job) => {
                            setJobIdToArchive(job.id);
                            setModal('archive');
                        },
                        unarchive: (job) => {
                            setJobIdToArchive(job.id);
                            setModal('unarchive');
                        },
                        edit: (job) => {
                            setSelectedJob(job);
                            setModal('edit');
                        },
                    }}
                    onRowClick={(record: JobResponse) => {
                        const isUnderManifest = Boolean(record.manifest?.id);
                        const to = isUnderManifest
                            ? `/manifests/${record.manifest?.id}/${record.id}`
                            : `/jobs/${record.id}`;
                        navigate(to);
                    }}
                    onChange={handleTableChange}
                    pagination={pagination}
                    currentSortDirection={sortDirection}
                    currentSortParam={sort}
                    optionalColumns={{
                        manifestId: true,
                        assignedTo: true,
                    }}
                />
            </Space>

            {selectedJob && (
                <AssignJobModal
                    jobData={{
                        jobId: selectedJob.id,
                        teamId: selectedJob.teamId,
                    }}
                    open={modal === 'assign'}
                    onSuccess={() => {
                        refetch();
                        closeModal();
                    }}
                    onClose={closeModal}
                />
            )}

            {selectedJob && (
                <EditJobModal
                    job={selectedJob}
                    open={modal === 'edit'}
                    onClose={() => {
                        setSelectedJob(null);
                        closeModal();
                    }}
                    onSuccess={refetch}
                />
            )}

            <ArchiveJobModal onArchive={handleArchiveJob} open={modal === 'archive'} onClose={closeModal} />
            <UnarchiveJobModal onUnarchive={handleUnarchiveJob} open={modal === 'unarchive'} onClose={closeModal} />

            <JobFiltersModal
                open={modal === 'filter'}
                onClose={closeModal}
                onClearAll={clearAll}
                onClearFilter={clearFilter}
                optionalFilters={{
                    assignedTo: true,
                    customer: true,
                }}
                filters={filters}
                onFiltersChange={handleFiltersChange}
            />
        </>
    );
};

export default JobsTab;
